/**
 * 添加 / 编辑 AI 供应商弹窗；可按供应商和模型名自动获取价格
 */
import { App, Button, Col, Form, Input, InputNumber, Modal, Row, Select, Switch, Typography } from 'antd'
import { SyncOutlined } from '@ant-design/icons'
import { useFetchModelPrice, useSaveProvider } from './hooks'
import type { AIProvider, ProviderFormValues } from './types'
import styles from './AIProviderPage.module.css'

const DEFAULT_VALUES: ProviderFormValues = {
  provider_name: '',
  api_base_url: '',
  api_key: '',
  model_name: '',
  temperature: 0.7,
  max_tokens: 4096,
  rate_limit: 60,
  is_active: false,
  input_cost_per_million: undefined,
  output_cost_per_million: undefined,
  cost_currency: 'USD'
}

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD (美元)' },
  { value: 'CNY', label: 'CNY (人民币)' }
]

interface ProviderFormModalProps {
  open: boolean
  // 为 null 时是新建
  provider: AIProvider | null
  onClose: () => void
}

// 编辑时不显示原密钥（出于安全考虑）
function initialValues(provider: AIProvider | null): ProviderFormValues {
  if (!provider) return DEFAULT_VALUES
  return {
    ...DEFAULT_VALUES,
    provider_name: provider.provider_name,
    api_base_url: provider.api_base_url,
    model_name: provider.model_name,
    temperature: provider.temperature,
    max_tokens: provider.max_tokens,
    rate_limit: provider.rate_limit,
    is_active: provider.is_active,
    input_cost_per_million: provider.input_cost_per_million ?? undefined,
    output_cost_per_million: provider.output_cost_per_million ?? undefined,
    cost_currency: provider.cost_currency === 'CNY' ? 'CNY' : 'USD'
  }
}

// 每次打开都重新挂载表单，从当前供应商初始化，关闭后状态不残留
export function ProviderFormModal({ open, provider, onClose }: Readonly<ProviderFormModalProps>) {
  if (!open) return null
  return <ProviderForm key={provider?.id ?? 'new'} provider={provider} onClose={onClose} />
}

function ProviderForm({ provider, onClose }: Readonly<Omit<ProviderFormModalProps, 'open'>>) {
  const { message } = App.useApp()
  const [form] = Form.useForm<ProviderFormValues>()
  const saveProvider = useSaveProvider()
  const fetchPrice = useFetchModelPrice()
  const isEdit = provider !== null
  const providerName = Form.useWatch('provider_name', form)
  const modelName = Form.useWatch('model_name', form)
  const currency = Form.useWatch('cost_currency', form)
  const canFetchPrice = Boolean(providerName && modelName)
  const amountPlaceholder = `请输入${currency === 'CNY' ? '人民币' : '美元'}金额`

  const handleFetchPrice = async () => {
    if (!canFetchPrice) {
      message.warning('请先填写供应商名称和模型名称')
      return
    }
    try {
      const price = await fetchPrice.mutateAsync({ modelName, providerName })
      if (price.found) {
        form.setFieldsValue({
          input_cost_per_million: price.input,
          output_cost_per_million: price.output,
          cost_currency: price.currency
        })
        message.success(`成功获取${price.fromLiteLLM ? ' LiteLLM ' : ''}价格信息`)
      } else {
        message.warning(price.message || '未找到该模型的价格信息')
      }
    } catch (error: any) {
      message.error('获取价格失败：' + (error?.message || '网络错误'))
    }
  }

  const handleSubmit = async () => {
    let values: ProviderFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    try {
      await saveProvider.mutateAsync({ id: provider?.id, values })
      message.success(isEdit ? '更新成功' : '添加成功')
      onClose()
    } catch (error) {
      console.error('提交失败:', error)
      message.error('操作失败')
    }
  }

  return (
    <Modal
      open
      title={isEdit ? '编辑供应商' : '添加供应商'}
      width={600}
      confirmLoading={saveProvider.isPending}
      onOk={() => void handleSubmit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" initialValues={initialValues(provider)}>
        <Form.Item label="供应商名称" name="provider_name" rules={[{ required: true, message: '请输入供应商名称' }]}>
          <Input placeholder="例如：OpenAI、Claude、百度文心" />
        </Form.Item>
        <Form.Item
          label="API基础URL"
          name="api_base_url"
          rules={[
            { required: true, message: '请输入API基础URL' },
            { type: 'url', message: '请输入有效的URL' }
          ]}
        >
          <Input placeholder="例如：https://api.openai.com/v1" />
        </Form.Item>
        <Form.Item
          label="API密钥"
          name="api_key"
          rules={[{ required: !isEdit, message: '请输入API密钥' }]}
          extra={isEdit ? '出于安全考虑，原密钥不会显示。如需修改请输入新密钥，留空则保持原密钥不变。' : undefined}
        >
          <Input.Password placeholder={isEdit ? '留空表示不修改密钥' : '请输入API密钥'} />
        </Form.Item>
        <Form.Item label="模型名称" name="model_name" rules={[{ required: true, message: '请输入模型名称' }]}>
          <Input placeholder="例如：gpt-4、claude-3-opus" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="温度参数" name="temperature">
              <InputNumber min={0} max={2} step={0.1} className={styles.fullWidth} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="最大Token数" name="max_tokens">
              <InputNumber min={1} max={100000} className={styles.fullWidth} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="速率限制(请求/分钟)" name="rate_limit">
              <InputNumber min={1} className={styles.fullWidth} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="成本货币" name="cost_currency">
              <Select aria-label="成本货币" options={CURRENCY_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="输入成本(每百万Token)" name="input_cost_per_million">
              <InputNumber min={0} precision={6} placeholder={amountPlaceholder} className={styles.fullWidth} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="输出成本(每百万Token)" name="output_cost_per_million">
              <InputNumber min={0} precision={6} placeholder={amountPlaceholder} className={styles.fullWidth} />
            </Form.Item>
          </Col>
        </Row>
        <div className={styles.priceRow}>
          <Button
            type="link"
            icon={<SyncOutlined />}
            loading={fetchPrice.isPending}
            disabled={!canFetchPrice}
            onClick={() => void handleFetchPrice()}
          >
            自动获取当前价格
          </Button>
          {!canFetchPrice && <Typography.Text type="secondary">请先填写供应商名称和模型名称</Typography.Text>}
        </div>
        <Form.Item label="是否激活" name="is_active" valuePropName="checked" extra="只有激活的供应商才能被选择和使用">
          <Switch checkedChildren="激活" unCheckedChildren="关闭" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
